/**
 * Tenant settings: retention and the API quota.
 *
 * Retention is a compliance decision rather than a preference, so each period says what
 * it governs and what its bounds are. The audit floor is explained rather than merely
 * enforced — an administrator who hits a validation error they do not understand assumes
 * the product is broken.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { Quota, RetentionPolicy, RetentionPolicyRequest, TenantResponse } from '@/lib/schemas';
import { Button, Card, ErrorState, Loading } from '@/components/ui';
import { useCan } from '@/hooks/usePermissions';

type PeriodKey = 'observation_days' | 'evidence_days' | 'report_days' | 'audit_days';

const FIELDS: { key: PeriodKey; label: string; help: string; min: number }[] = [
  {
    key: 'observation_days',
    label: 'Observations',
    help: 'Collection attempts and the claims extracted from them.',
    min: 1,
  },
  {
    key: 'evidence_days',
    label: 'Evidence',
    help: 'Captured responses. Must be at least as long as observations, or a claim would outlive the evidence behind it.',
    min: 1,
  },
  { key: 'report_days', label: 'Reports', help: 'Generated documents.', min: 1 },
  {
    key: 'audit_days',
    label: 'Audit log',
    help: 'The record of what the platform did with personal data. Floored at 365 days: shortening this deletes the evidence of your own compliance.',
    min: 365,
  },
];

/** The tenant settings view. */
export function SettingsPage(): ReactElement {
  const canManage = useCan('manage_tenant_settings');
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<PeriodKey, number>>({
    observation_days: 400,
    evidence_days: 400,
    report_days: 90,
    audit_days: 2555,
  });

  const policy = useQuery({
    queryKey: ['retention'],
    queryFn: () => request<RetentionPolicy>('/retention'),
    enabled: canManage,
  });
  const quota = useQuery({ queryKey: ['quota'], queryFn: () => request<Quota>('/quota') });

  // The workspace these settings belong to. `GET /tenant` was published and nothing
  // displayed it, so a screen headed "Settings" never said whose. On a platform where the
  // slug is half of every credential, that is worth three lines.
  const tenant = useQuery({
    queryKey: ['tenant'],
    queryFn: () => request<TenantResponse>('/tenant'),
  });

  useEffect(() => {
    if (policy.data) {
      setDraft({
        observation_days: policy.data.observation_days,
        evidence_days: policy.data.evidence_days,
        report_days: policy.data.report_days,
        audit_days: policy.data.audit_days,
      });
    }
  }, [policy.data]);

  const save = useMutation({
    mutationFn: () => request<RetentionPolicy, RetentionPolicyRequest>('/retention', { method: 'PUT', body: draft }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['retention'] }),
  });

  if (!canManage) {
    return (
      <p className="text-sm text-muted">
        Tenant settings require the <code>manage_tenant_settings</code> permission, which your
        role does not hold.
      </p>
    );
  }
  if (policy.isLoading) return <Loading />;
  if (policy.error) return <ErrorState error={policy.error} />;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Settings</h1>

      {tenant.data != null && (
        <Card title="Workspace">
          <dl className="grid gap-3 p-4 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted">Name</dt>
              <dd className="mt-0.5">{tenant.data.name}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted">Slug</dt>
              <dd className="mt-0.5 font-mono text-xs">{tenant.data.slug}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted">Status</dt>
              <dd className="mt-0.5">{tenant.data.status}</dd>
            </div>
          </dl>
        </Card>
      )}

      <Card title="Retention">
        <div className="space-y-4">
          {FIELDS.map((field) => (
            <label key={field.key} className="block text-sm">
              <span className="font-medium">{field.label}</span>
              <input
                type="number"
                min={field.min}
                max={3650}
                value={draft[field.key]}
                onChange={(event) =>
                  setDraft((previous) => ({ ...previous, [field.key]: Number(event.target.value) }))
                }
                className="ml-3 w-28 rounded border border-line bg-surface px-2 py-1 text-sm tabular-nums"
              />
              <span className="ml-2 text-xs text-muted">days</span>
              <span className="mt-1 block text-xs text-muted">{field.help}</span>
            </label>
          ))}
          {save.error != null && <ErrorState error={save.error} />}
          <Button tone="primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save retention policy'}
          </Button>
          <p className="text-xs text-muted">
            The nightly sweep deletes in dependency order — reports, claims, observations, then
            captured bodies. A purged body leaves a tombstone, so a citation that outlives its
            evidence says why rather than failing.
          </p>
        </div>
      </Card>

      <Card title="API quota">
        {quota.data ? (
          quota.data.requests_per_minute === 0 ? (
            <p className="text-sm text-muted">
              No API quota is configured. Requests are limited only by what the sources
              themselves permit.
            </p>
          ) : (
            <p className="text-sm">
              <span className="font-mono tabular-nums">{quota.data.used_this_minute}</span> of{' '}
              <span className="font-mono tabular-nums">{quota.data.requests_per_minute}</span>{' '}
              requests used this minute · {quota.data.remaining} remaining
            </p>
          )
        ) : (
          <Loading />
        )}
      </Card>
    </div>
  );
}
